import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useEffect, useRef, useState } from "react";
import { useSpinDelay } from "spin-delay";
import { containerStyles, Page } from "#/components/page";
import {
  type SchoolSearchResult,
  submitAddSchool,
  submitSearchSchoolByBrin,
} from "#/modules/school/add-school.functions";
import { SchoolSearchResultTile } from "#/modules/school/components/school-search-result-tile";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/add-school")({
  component: AddSchoolPage,
});

function AddSchoolPage() {
  const { locale } = Route.useParams();
  const searchSchoolFn = useServerFn(submitSearchSchoolByBrin);
  const addSchoolFn = useServerFn(submitAddSchool);

  const [brin, setBrin] = useState("");
  const [result, setResult] = useState<SchoolSearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSubmittedBrinRef = useRef("");

  const trimmedBrin = brin.trim();
  const hasSearchableBrin = trimmedBrin.length >= 4 && trimmedBrin.length <= 6;
  const showSearchingSpinner = useSpinDelay(isSearching);

  const hasError = result != null && "formError" in result;
  const schools =
    hasSearchableBrin && result && "schools" in result && Array.isArray(result.schools)
      ? result.schools
      : [];
  const searchWasTriggered = hasSearchableBrin && result != null && "schools" in result;

  useEffect(() => {
    const trimmed = brin.trim();
    if (trimmed.length < 4 || trimmed.length > 6) return;
    if (trimmed === lastSubmittedBrinRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      lastSubmittedBrinRef.current = trimmed;
      setIsSearching(true);
      try {
        const searchResult = await searchSchoolFn({ data: { brin: trimmed } });
        if (searchResult) setResult(searchResult);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [brin, searchSchoolFn]);

  async function handleAdd(schoolBrin: string) {
    setIsAdding(true);
    try {
      const addResult = await addSchoolFn({ data: { brin: schoolBrin, locale } });
      if (addResult) {
        setResult(addResult);
      }
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <Page>
      <Page.Header>
        <Page.Title>{m["common.add_another_school"]()}</Page.Title>
      </Page.Header>
      <Page.Body className={cn(containerStyles({ container: "default" }), "pb-10")}>
        <div className="grid w-full max-w-3xl gap-5">
          <Card>
            <CardHeader>
              <CardTitle>{m["school.add_school.search_title"]()}</CardTitle>
              <CardDescription>{m["school.add_school.search_description"]()}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <InputGroup>
                  <InputGroupAddon align="inline-end">
                    {showSearchingSpinner ? <IconSvgSpinnersRingResize /> : null}
                  </InputGroupAddon>
                  <InputGroupInput
                    id="brin"
                    name="brin"
                    value={brin}
                    onChange={(e) => setBrin(e.currentTarget.value)}
                    placeholder={m["school.add_school.brin_placeholder"]()}
                    aria-invalid={hasError || undefined}
                    aria-describedby={hasError ? "add-school-error" : undefined}
                    className={hasError ? "border-red-300 focus-visible:ring-red-300" : undefined}
                  />
                </InputGroup>
                {hasError && (
                  <p id="add-school-error" className="text-sm font-medium text-red-700">
                    {result.formError}
                  </p>
                )}
              </div>

              {!hasError && schools.length > 0 && (
                <div className="mt-4 space-y-2 border-t border-black/7 pt-4">
                  {schools.map((school) => (
                    <SchoolSearchResultTile
                      key={school.brin}
                      school={school}
                      buttonLabel={
                        school.isRegistered
                          ? m["school.add_school.already_linked_cta"]()
                          : m["common.actions.add"]()
                      }
                      buttonDisabled={isAdding || Boolean(school.isRegistered)}
                      onButtonClick={() => handleAdd(school.brin)}
                    />
                  ))}
                </div>
              )}

              {!hasError && searchWasTriggered && schools.length === 0 && (
                <p className="text-muted-foreground mt-4 border-t border-black/7 pt-4 text-sm">
                  {m["school.add_school.empty_results"]()}
                </p>
              )}
            </CardContent>
          </Card>

          {hasError && (
            <Alert variant="destructive" className="rounded-xl">
              <IconMaterialSymbolsErrorCircleRoundedOutlineSharp />
              <AlertDescription>{result.formError}</AlertDescription>
            </Alert>
          )}
        </div>
      </Page.Body>
    </Page>
  );
}
